/**
 * Activity log (§7.2), shared by the owner and renter entry points.
 *
 * §7.6: the window is always stated. An empty log with no window reads as a
 * failure — "nothing happened" is only reassuring if you know over what period.
 */

import { useState } from 'react';
import { EMPTY_STATES } from '@pm/shared';
import { useApi } from '../../hooks/useApi';
import { useBotOutlet } from '../botOutlet';
import type { ActivityResponse } from '../../api/types';
import { Card, Field, Loading } from '../../components/ui';
import { ErrorView } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { BotTime } from '../../components/TimeDisplay';
import { humanize, interpolate, localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

const WINDOWS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'Everything' },
];

export function ActivityLog() {
  const { bot, rental } = useBotOutlet();
  const [windowKey, setWindowKey] = useState('7d');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const log = useApi<ActivityResponse>(routes.bots.activity(bot.id), {
    query: {
      window: windowKey,
      limit: 50,
      cursor,
      // A renter sees the log scoped to their own rental; the server enforces it.
      ...(rental ? { rental_id: rental.id } : {}),
    },
  });

  const entries = log.data?.data ?? [];
  const w = log.data?.window;

  return (
    <Card
      title="Activity"
      actions={
        <Field id="act-window" label="Window">
          <select
            id="act-window"
            className="select"
            value={windowKey}
            onChange={(e) => {
              setWindowKey(e.target.value);
              setCursor(undefined);
            }}
          >
            {WINDOWS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      }
    >
      {log.initialLoading ? <Loading label="Loading the log" /> : null}
      {log.problem ? <ErrorView problem={log.problem} onRetry={log.reload} /> : null}

      {w ? (
        <p className="muted">
          Showing {localDateTime(w.from)} to {localDateTime(w.to)}.
        </p>
      ) : null}

      {!log.initialLoading && entries.length === 0 ? (
        <EmptyState
          heading="Nothing in this window"
          body={
            w
              ? interpolate(EMPTY_STATES.activityEmptyWindow.bodyTemplate, {
                  from: localDateTime(w.from),
                  to: localDateTime(w.to),
                })
              : undefined
          }
        />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">What</th>
                <th scope="col">Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.seq}>
                  <td>
                    <BotTime iso={e.created_at} timeZone={bot.timezone} />
                  </td>
                  <td>
                    {e.actor_handle ? `@${e.actor_handle}` : 'system'}{' '}
                    <span className="muted">({e.actor_role})</span>
                  </td>
                  <td>{humanize(e.action)}</td>
                  <td>
                    {e.detail}
                    {e.reason ? <span className="muted"> — {e.reason}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {log.data?.next_cursor ? (
            <button
              type="button"
              className="btn"
              onClick={() => setCursor(log.data?.next_cursor ?? undefined)}
            >
              Load more
            </button>
          ) : null}
        </>
      )}
    </Card>
  );
}
